import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Coins, Store, Gift } from "lucide-react";
import { toast } from "sonner";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2500;

export default function CreditReturnPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const orderId = params.get("order_id");
  const cancelled = params.get("status") === "cancelled";
  const stripe = params.get("provider") === "stripe";

  // 'polling' | 'success' | 'cancelled' | 'pending' | 'error'
  const [state, setState] = useState(cancelled ? "cancelled" : "polling");
  const [info, setInfo] = useState(null);
  const [retry, setRetry] = useState(0);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!orderId) { setState(cancelled ? "cancelled" : "error"); return; }
    if (cancelled && !retry) { setState("cancelled"); return; }
    let active = true;
    let timer;
    setState("polling");
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${orderId}`);
        if (!active) return;
        if (data.payment_status === "paid" && data.credited === true) {
          setInfo(data);
          setState("success");
          await refreshRef.current();
          toast.success(`${Number(data.credits).toLocaleString()} credits added!`);
          return;
        }
        if (data.status === "expired" || data.status === "failed" || data.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch {
        if (!active) return;
        if (attempts >= MAX_ATTEMPTS) { setState("error"); return; }
      }
      if (attempts >= MAX_ATTEMPTS) { setState("pending"); return; }
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [orderId, cancelled, retry]);

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-20">
      <div className="hud-pill rounded-2xl p-8 text-center relative overflow-hidden" data-testid="credit-return">
        <div className="absolute inset-0 holo-scan opacity-10 pointer-events-none" />

        {state === "polling" && (
          <div data-testid="credit-return-polling">
            <Loader2 className="h-12 w-12 mx-auto text-cyan-300 animate-spin" />
            <h1 className="font-display text-2xl text-white tracking-[0.05em] mt-4">CONFIRMING PAYMENT…</h1>
            <p data-testid="credit-return-confirmation-detail" className="text-white/50 text-sm mt-1">{stripe ? "Verifying your card payment with Stripe." : "Verifying your cryptocurrency payment."}</p>
          </div>
        )}

        {state === "success" && (
          <div data-testid="credit-return-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-300" />
            <h1 className="font-display text-3xl text-white tracking-[0.05em] mt-4">PAYMENT COMPLETE</h1>
            <div data-testid="credit-return-amount" className="inline-flex items-center gap-2 mt-3 font-mono text-amber-300 text-lg">
              <Coins className="h-5 w-5" /> +{Number(info?.credits || 0).toLocaleString()} credits
            </div>
            <p className="text-white/50 text-sm mt-2">{info?.package_name ? `${info.package_name} pack` : "Your credits"} have been added to your balance.</p>
            <div className="flex gap-3 justify-center mt-6">
              <Button data-testid="credit-return-store" onClick={() => navigate("/store")} className="bg-cyan-400 text-black hover:bg-cyan-300"><Store className="h-4 w-4 mr-1.5" /> Go to Store</Button>
              <Button data-testid="credit-return-redeem" variant="outline" onClick={() => navigate("/redeem")} className="border-white/15 text-white"><Gift className="h-4 w-4 mr-1.5" /> Redeem</Button>
            </div>
          </div>
        )}

        {state === "cancelled" && (
          <div data-testid="credit-return-cancelled">
            <XCircle className="h-12 w-12 mx-auto text-white/40" />
            <h1 className="font-display text-2xl text-white tracking-[0.05em] mt-4">CHECKOUT CANCELLED</h1>
            <p data-testid="credit-return-cancel-message" className="text-white/50 text-sm mt-1">Checkout was closed. If you completed payment, check its status before starting another order.</p>
            <div className="flex gap-3 justify-center mt-6">
              <Button data-testid="credit-return-cancel-store" onClick={() => navigate("/store")} className="bg-cyan-400 text-black hover:bg-cyan-300">Back to Store</Button>
              <Button data-testid="credit-return-cancel-redeem" variant="outline" onClick={() => navigate("/redeem")} className="border-white/15 text-white">Redeem</Button>
            </div>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="credit-return-pending">
            <Loader2 className="h-12 w-12 mx-auto text-amber-300" />
            <h1 className="font-display text-2xl text-white tracking-[0.05em] mt-4">STILL PROCESSING</h1>
            <p data-testid="credit-return-pending-detail" className="text-white/55 text-sm mt-2">{stripe ? "Stripe has not confirmed delivery yet. Your order is saved. Check again here or from your purchase history." : "Crypto transfers can take 15–20 minutes. Partial payments receive proportional credits once verified."}</p>
            <Button data-testid="credit-return-pending-store" onClick={() => navigate("/store")} className="mt-6 bg-cyan-400 text-black hover:bg-cyan-300">Back to Store</Button>
          </div>
        )}

        {state === "error" && (
          <div data-testid="credit-return-error">
            <XCircle className="h-12 w-12 mx-auto text-red-400" />
            <h1 className="font-display text-2xl text-white tracking-[0.05em] mt-4">COULDN'T CONFIRM</h1>
            <p data-testid="credit-return-error-detail" className="text-white/50 text-sm mt-1">We couldn't verify this order. If you were charged, don't pay again. Check your saved order in <Link data-testid="credit-return-history" to="/profile" className="text-cyan-300 underline">purchase history</Link>.</p>
            <Button data-testid="credit-return-error-store" onClick={() => navigate("/store")} className="mt-6 bg-cyan-400 text-black hover:bg-cyan-300">Back to Store</Button>
          </div>
        )}
        {orderId && ["pending", "error", "cancelled"].includes(state) && <Button data-testid="credit-return-retry" variant="outline" className="mt-4 border-white/20 text-white" onClick={() => setRetry(n => n + 1)}>Check payment status</Button>}
        {stripe && <p data-testid="credit-return-powered-by" className="text-[11px] text-white/50 mt-5">Powered by Steele Industries</p>}
      </div>
    </div>
  );
}
